import { Fragment, FunctionComponent, useMemo } from 'react';
import { useFragment, graphql } from 'react-relay';

import { SeoLink } from 'dibs-elements/exports/SeoLink';
import { LazyImage, LazyImageProps } from '../../../LazyImage/LazyImage';
import { ImageSizeType } from '../../../types/StyleSizes';
import styles from '../../styles.scss';

import { getVideoImgProps, getPhotoImgProps } from './helpers';
import { usePhotoImgAltText } from '../../../helpers/usePeriodGroupHelpers';
import { usePeriodGroupValue } from '../../../helpers/usePeriodGroupValue';
import { LazyThumbVideo } from './LazyThumbVideo';

import { SwiperItem_photo$key } from './__generated__/SwiperItem_photo.graphql';
import { SwiperItem_video$key } from './__generated__/SwiperItem_video.graphql';
import { SwiperItem_itemSearch$key } from './__generated__/SwiperItem_itemSearch.graphql';

export interface ItemProps {
    disableLinks?: boolean;
    onClick?: () => void;
    pdpUrl: string;
    title: string;
    containerClassName?: string;
    srcSetSizes?: string;
    srcSetWidths?: Array<number>;
    srcSetQuality?: number;
    useLoFiLazyLoader: boolean;
    imageSize: ImageSizeType;
    imageLoadVerticalOffset?: number;
    onContentLoaded?: () => void;
    video?: SwiperItem_video$key | null;
}

interface Props extends ItemProps {
    index: number;
    useEagerImageLoad: boolean;
    photo: SwiperItem_photo$key;
    itemSearch: SwiperItem_itemSearch$key;
}

const photoQuery = graphql`
    fragment SwiperItem_photo on ItemPhoto {
        placeholder
        smallPath
        masterOrZoomPath
    }
`;

const videoQuery = graphql`
    fragment SwiperItem_video on VideoVimeo {
        videoThumb: thumbnail(size: w295h166, pad: false)
        height
        width
        fileByVideoSize {
            link
        }
    }
`;

const itemSearchQuery = graphql`
    fragment SwiperItem_itemSearch on ItemSearchQueryConnection {
        ...usePeriodGroupValue_itemSearch
    }
`;

export const SwiperItem: FunctionComponent<Props> = ({
    photo: photoRef,
    video: videoRef,
    itemSearch: itemSearchRef,
    index,
    useEagerImageLoad,
    containerClassName,
    disableLinks,
    onClick,
    pdpUrl,
    srcSetSizes,
    srcSetWidths,
    srcSetQuality,
    imageSize,
    title,
    imageLoadVerticalOffset,
    onContentLoaded,
    useLoFiLazyLoader,
}) => {
    const photo = useFragment(photoQuery, photoRef || null);
    const video = useFragment(videoQuery, videoRef || null);
    const itemSearch = useFragment(itemSearchQuery, itemSearchRef || null);
    const periodGroup = usePeriodGroupValue({ itemSearch });
    const photoAltText = usePhotoImgAltText(title, periodGroup);

    const item = useMemo(() => {
        let lazyImageProps: Partial<LazyImageProps> | undefined = undefined;
        let content = <Fragment />;

        if (photo) {
            lazyImageProps = getPhotoImgProps(photo, srcSetSizes, srcSetWidths, srcSetQuality);
            content = (
                <img
                    className={styles.swiperImage}
                    alt={photoAltText}
                    data-tn="product-image"
                    {...lazyImageProps.srcSetAttributes}
                    src={lazyImageProps.src}
                />
            );
        }

        if (video) {
            lazyImageProps = lazyImageProps ?? getVideoImgProps(video);
            if (video)
                content = (
                    <LazyThumbVideo video={video} poster={lazyImageProps.src}>
                        {content}
                    </LazyThumbVideo>
                );
        }

        return useEagerImageLoad ? (
            content
        ) : (
            <Fragment>
                <LazyImage
                    className={styles.swiperImage}
                    alt={title}
                    offsetVertical={imageLoadVerticalOffset}
                    onImageLoad={onContentLoaded}
                    useLoFiLazyLoader={useLoFiLazyLoader}
                    imageSize={imageSize}
                    {...lazyImageProps}
                />
                {index === 0 && <noscript>{content}</noscript>}
            </Fragment>
        );
    }, [
        video,
        photo,
        imageLoadVerticalOffset,
        imageSize,
        index,
        onContentLoaded,
        srcSetQuality,
        srcSetSizes,
        srcSetWidths,
        title,
        useEagerImageLoad,
        useLoFiLazyLoader,
        photoAltText,
    ]);

    const itemWithLink = (
        <SeoLink
            dataTn="thumb-image-link"
            linkData={{ path: pdpUrl, isLinkable: false }}
            onClick={onClick}
            className={styles.swiperLink}
        >
            <div className={containerClassName} data-tn="product-image-container">
                {item}
            </div>
        </SeoLink>
    );

    return disableLinks ? item : itemWithLink;
};
